import { useCallback, useEffect, useMemo } from 'react';
import { RecentCallsResponse } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2/call_intelligence.pb';
import { CallDetail } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { useQueryClient } from 'react-query';
import {
  CallStatusEnum,
  checkDemoUser,
  FilterTypeEnum,
  type GetCallsOverviewIO,
  IncludesAnalysis,
  type RecentCallsIO,
  useGetCallsOverviewQuery,
  useRecentCallsQuery,
} from '@frontend/api-call-intel';
import { getUser, hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import { PanelEvents, useCallInsightController } from '@frontend/call-insight-panel';
import { useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { PATIENT_INSIGHT_PERMISSION } from '../constants';

const RECENT_CALLS_ANALYSIS = [
  IncludesAnalysis.CALL_CATEGORY,
  IncludesAnalysis.CALL_SUMMARY,
  IncludesAnalysis.CALL_SENTIMENT,
  IncludesAnalysis.FOLLOWUP_TASKS,
  IncludesAnalysis.CALL_APPOINTMENT_TYPE,
];

const TRENDS_ANALYSIS = [IncludesAnalysis.CALL_CATEGORY, IncludesAnalysis.CALL_SENTIMENT];

interface UsePatientInsightsRequestsProps {
  dateRange: { start: string; end: string };
  personId: string;
  showTrends: boolean;
  isDemoView?: boolean;
}

export const usePatientInsightsRequests = ({
  dateRange,
  personId,
  showTrends,
  isDemoView,
}: UsePatientInsightsRequestsProps) => {
  const alert = useAlert();
  const { t } = useTranslation('callIntelInsights');
  const { selectedLocationIds: scopedLocationIds } = useAppScopeStore();
  const queryClient = useQueryClient();

  const selectedLocationIds = useMemo(() => {
    if (isDemoView || isWeaveUser() || checkDemoUser(getUser()?.username ?? '')) return scopedLocationIds;
    return scopedLocationIds.filter((locationId) => hasSchemaACL(locationId, PATIENT_INSIGHT_PERMISSION));
  }, [scopedLocationIds, isDemoView]);

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isServiceQualityEnabled = getFeatureFlagValue('enable-service-quality');
  const hasServiceQualityAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ_SERVICE_QUALITY]);

  const { onEvent, offEvent } = useCallInsightController({
    source: 'patientInsightsContactPanel',
  });

  const recentCallsRequest = useMemo<RecentCallsIO['input']>(() => {
    const analysis = [
      ...RECENT_CALLS_ANALYSIS,
      ...(isServiceQualityEnabled && hasServiceQualityAccess ? [IncludesAnalysis.CALL_SERVICE_QUALITY] : []),
      IncludesAnalysis.CALL_APPOINTMENTS,
    ];

    return {
      locations: selectedLocationIds,
      personId,
      includesAnalysis: analysis,
      limit: 1,
    };
  }, [personId, selectedLocationIds, isServiceQualityEnabled, hasServiceQualityAccess]);

  const callOverviewFilter = useMemo<GetCallsOverviewIO['input']['filter']>(
    () => ({
      [FilterTypeEnum.FILTER_BY_DATE_RANGE]: { dateRange },
      [FilterTypeEnum.FILTER_BY_LOCATION]: { locations: selectedLocationIds },
      [FilterTypeEnum.FILTER_BY_CALL_STATUS]: {
        callStatus: [CallStatusEnum.CALL_STATUS_COMPLETE],
      },
      [FilterTypeEnum.FILTER_BY_PERSON_ID]: { personId: [personId] },
    }),
    [personId, dateRange, selectedLocationIds]
  );

  const callTrendsRequest = useMemo<GetCallsOverviewIO['input']>(
    () => ({
      filter: callOverviewFilter,
      includesAnalysis: TRENDS_ANALYSIS,
      isCallTrendDemo: false, // It will be set later by demo interceptor on demo view
    }),
    [callOverviewFilter]
  );

  const {
    data: lastCallDetail,
    isLoading: isLastCallLoading,
    queryKey,
  } = useRecentCallsQuery({
    request: recentCallsRequest,
    requestKeysToOmitFromQueryKey: ['includesAnalysis', 'includesHydration', 'limit'],
    options: {
      enabled: !!(selectedLocationIds.length && personId),
      select: (data) => data.calls?.[0],
      onError: () => {
        alert.error(t("Couldn't fetch recent call. Please try again."));
      },
    },
  });

  const {
    data: callTrends,
    isLoading: isCallTrendsLoading,
    refetch: refetchCallTrends,
  } = useGetCallsOverviewQuery({
    request: callTrendsRequest,
    options: {
      enabled: showTrends,
      onError: () => {
        alert.error(t("Couldn't fetch overview details. Please try again."));
      },
    },
  });

  const handleUpdateCallAnalysis = useCallback(
    ({ call: newCall }: PanelEvents['callAnalysisUpdated']) => {
      queryClient.setQueryData<RecentCallsResponse>(queryKey, (old) => {
        const prevCallDetail = old?.calls?.[0];
        return {
          calls: [
            {
              ...prevCallDetail,
              call: newCall,
            },
          ] as CallDetail[],
        };
      });
      // silently refresh the trends
      refetchCallTrends();
    },
    [queryClient, queryKey, refetchCallTrends]
  );

  useEffect(() => {
    onEvent('callAnalysisUpdated', handleUpdateCallAnalysis);

    return () => {
      offEvent('callAnalysisUpdated', handleUpdateCallAnalysis);
    };
  }, [handleUpdateCallAnalysis, offEvent, onEvent]);

  return { lastCallDetail, isLastCallLoading, callTrends, isCallTrendsLoading };
};
